import { definedMapWithDefault } from "collection-utils";
import { getGoogleSheetsSourceMetadata, getSourceMetadata } from "common/components/SerializedApp";
import {
    daysLeftInTrial,
    daysSinceTrialStart,
    getAppEminenceFlags,
    isUserData,
    loadBuilderApp,
    Organization,
    UserDataAndState,
    fourteenDayMax,
    AuthenticationMethod,
    isAuthenticationMethodPublic,
} from "common/Database";
import { OwnerKind } from "common/Database/owner-kind";
import { getFeatureSetting } from "common/feature-settings";
import { Icon } from "common/render/icon";
import { dontAwait } from "common/Support";
import { VisualStyle } from "designer/lib/dropdown-types";
import { showBasicActionModal, showErrorModal } from "designer/ui/smart/modals/modals";
import { reloadPublishedAppDataFromSheet } from "lib/firebase-functions";
import { pickGoogleSheet } from "lib/google-sheets-source";
import { getBuilderAppDatabase } from "lib/web-database";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { BuilderTheme } from "sharedUI/styles";
import styled, { withTheme } from "styled-components";
import { MappedThunk } from "../../../../actions/action-thunk";
import * as actions from "../../../../actions/actions";
import * as appsActions from "../../../../actions/apps-actions";
import { trackEvent } from "../../../../analytics";
import * as routes from "../../../../routes";
import {
    getAppOwner,
    OrganizationsState,
    RootState,
    TemplateStoreState,
    TileDescription,
} from "../../../../store/types";
import { decodeCopyTemplateMessage } from "../../../lib/copy-template-message";
import { getUsageString, openApp } from "../orgs-lib";
import { LoadingIndicator } from "../orgs-styles";
import AppTile from "./app-tile";

const LoadingWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: ${p => p.theme.bgColorLightTransparent};
`;

interface Props extends RouteComponentProps {
    readonly app: TileDescription | undefined;
    readonly appID: string;
    readonly userData: UserDataAndState;
    readonly organizations: OrganizationsState;
    readonly isSample: boolean;
    readonly isSelected?: boolean;
    readonly onDeleteApp?: (appID: string, title: string) => void;
    readonly onDuplicateApp?: (appID: string) => void;
    readonly onTransferToOrganization?: (appID: string, org: Organization) => void;
    readonly onTransferToUser?: (appID: string, withCopy: boolean) => void;
    readonly onMakeSupportCode?: (appID: string) => void;

    readonly copySampleApp: MappedThunk<typeof actions.copySampleApp>;
    readonly unloadApps: typeof appsActions.unloadApps;

    readonly templateStore: TemplateStoreState;
    readonly theme: BuilderTheme;

    readonly isFavorite?: boolean;
    readonly setIsFavorite?: (isFavorite: boolean) => void;
}
interface State {
    readonly menuOpen: boolean;
    readonly loading: boolean;
    readonly copyingSample: boolean;
}

interface MenuItem {
    content: string;
    onClick: () => void;
    style?: VisualStyle;
}

class SerializedAppTileImpl extends React.PureComponent<Props, State> {
    public state: State = {
        menuOpen: false,
        loading: false,
        copyingSample: false,
    };

    private onClick = async () => {
        if (this.state.menuOpen) return;

        const { app, appID } = this.props;
        if (app?.requiresSheetReconnect === true) {
            const db = getBuilderAppDatabase(appID);
            const builderApp = await loadBuilderApp(db, appID);
            if (builderApp === undefined) {
                await showErrorModal("Cannot load the app.");
                return;
            }

            const sourceMetadata = getGoogleSheetsSourceMetadata(getSourceMetadata(builderApp.appDescription));
            if (sourceMetadata === undefined) {
                await showErrorModal("There's no Google Sheet connected to this app.");
                return;
            }

            await showBasicActionModal(
                "Connect Google Sheet",
                `Please connect the Google Sheet "${sourceMetadata.title}" to Glide`,
                "OK"
            );

            const fileID = await pickGoogleSheet();
            if (fileID === undefined) return;

            if (fileID !== sourceMetadata.id) {
                await showErrorModal("That's the wrong spreadsheet.");
                return;
            }

            // This will reset the flag in the app tile
            dontAwait(reloadPublishedAppDataFromSheet(appID, true, true));
        }

        await this.openApp(appID);
    };

    private openApp = async (appID: string): Promise<void> => {
        const { userData, history, unloadApps } = this.props;
        await openApp(appID, userData, history, unloadApps);
    };

    private onOpenExternally = () => {
        if (this.props.app?.url === undefined) return;

        window.open(this.props.app.url, "_blank");
    };

    private listenForCopyTemplateMessage = (message: MessageEvent) => {
        if (message.origin !== window.location.origin) return;

        const copyMessage = decodeCopyTemplateMessage(message.data);
        if (copyMessage === undefined) return;

        const { templateID } = copyMessage;
        const { appID, templateStore } = this.props;
        if (templateID !== appID) return;
        const storeInfo = templateStore[appID];

        if (storeInfo !== undefined) {
            trackEvent("buy template", { app_id: templateID, price: storeInfo.pricing.value });
        }
        this.onCopySample(true);
    };

    private onCopySample = (elideAccessChecks: boolean = false) => {
        if (this.state.copyingSample) return;
        this.setState({ loading: true, copyingSample: true });

        if (elideAccessChecks === false) {
            // We expect that listenForCopyTemplateMessage will call us again
            // with elideAccessChecks = true, and that only listenForCopyTemplateMessage
            // will do this.
            //
            // But we don't start off with any listeners. We need to set up listenForCopyTemplateMessage
            // to call us back once the template is purchased.
            window.addEventListener("message", this.listenForCopyTemplateMessage);
        }

        // copySampleApp needs to return undefined on failure.
        // This could be anything from internal system error to
        // unlicensed copy attempts.
        dontAwait(
            this.props.copySampleApp(
                this.props.appID,
                appID => {
                    this.setState({ loading: false, copyingSample: false });
                    if (appID === undefined) return;
                    this.props.history.push(routes.app(appID));
                },
                elideAccessChecks
            )
        );
    };

    public componentWillUnmount() {
        window.removeEventListener("message", this.listenForCopyTemplateMessage);
    }

    public render(): React.ReactNode {
        const {
            app,
            isSample,
            onDeleteApp,
            onDuplicateApp,
            onTransferToOrganization,
            onTransferToUser,
            onMakeSupportCode,
            appID,
            userData,
            organizations,
            isSelected,
            theme,
            isFavorite,
            setIsFavorite,
        } = this.props;
        const owner = getAppOwner(appID, userData, organizations);

        let content: React.ReactNode;
        let menu: MenuItem[] | undefined;
        let badge: string | undefined;
        if (app !== undefined) {
            if (!isSample) {
                menu = [];
                menu.push({ content: "Open App", onClick: this.onOpenExternally });
                const notProTrial =
                    owner !== undefined && !getAppEminenceFlags(owner, appID, undefined).isPrivateProTrial;
                if (onDuplicateApp !== undefined && notProTrial) {
                    menu.push({
                        content: "Duplicate…",
                        onClick: () => onDuplicateApp(appID),
                    });
                }
                if (
                    isUserData(userData) &&
                    owner !== undefined &&
                    getAppEminenceFlags(owner, appID, undefined).canTransferToOrg &&
                    onTransferToOrganization !== undefined
                ) {
                    if (owner.kind !== OwnerKind.Organization || owner.memberUserIDs[0] === userData.id) {
                        for (const org of organizations) {
                            menu.push({
                                content: `Transfer to ${org.displayName}…`,
                                onClick: () => onTransferToOrganization(appID, org),
                            });
                        }
                    }
                }
                if (onMakeSupportCode !== undefined && getFeatureSetting("supportCodes")) {
                    menu.push({
                        content: "Copy support link…",
                        onClick: () => {
                            onMakeSupportCode(appID);
                        },
                    });
                }
                if (onDeleteApp !== undefined) {
                    menu.push({
                        content: "Delete",
                        onClick: () => onDeleteApp(appID, app.title),
                        style: "destructive",
                    });
                }
                if (owner?.kind === OwnerKind.Organization && owner.isAgency && onTransferToUser !== undefined) {
                    menu.push({
                        content: "Transfer to user…",
                        onClick: () => onTransferToUser(appID, false),
                    });
                    menu.push({
                        content: "Copy to user…",
                        onClick: () => onTransferToUser(appID, true),
                    });
                }
            }

            let eminenceBadge: string | undefined;
            if (owner !== undefined) {
                if (getAppEminenceFlags(owner, appID, undefined).isPrivateProTrial) {
                    const daysLeft = daysLeftInTrial(daysSinceTrialStart(owner, appID, Date.now()), fourteenDayMax);
                    if (daysLeft !== undefined) {
                        eminenceBadge = `${daysLeft} days`;
                    }
                } else {
                    eminenceBadge = getAppEminenceFlags(owner, appID, undefined).badge;
                }
            }
            if (!isSample && owner !== undefined && eminenceBadge !== undefined) {
                badge = eminenceBadge.toUpperCase();
            }

            content = <Icon size={64} clipCorners={true} color={app.primaryAccentColor} image={app.iconImage} />;
        }

        if (this.state.loading) {
            content = (
                <div>
                    {content}
                    <div>
                        <LoadingWrapper>
                            <LoadingIndicator />
                        </LoadingWrapper>
                    </div>
                </div>
            );
        }

        let caption = undefined;
        let isPrivateProTrial = false;
        if (!isSample && owner !== undefined) {
            const eminenceFlags = getAppEminenceFlags(owner, appID, undefined);
            const isPublic = isAuthenticationMethodPublic(app?.authenticationKind ?? AuthenticationMethod.None);
            caption = getUsageString(app?.analytics, eminenceFlags, isPublic);
            isPrivateProTrial = eminenceFlags.isPrivateProTrial;
        }
        return (
            <AppTile
                name={definedMapWithDefault(app, "Loading…", a => a.title)}
                dimBackground={false}
                onClick={this.onClick}
                onOpenExternally={this.onOpenExternally}
                menu={menu}
                isSelected={isSelected === true}
                caption={caption}
                badge={badge}
                isFavorite={isFavorite}
                setIsFavorite={setIsFavorite}
                canHover={!this.state.loading}
                accentColor={app?.primaryAccentColor ?? "white"}
                badgeColor={isPrivateProTrial ? theme.accentGradient : undefined}>
                {content}
            </AppTile>
        );
    }
}

const mapDispatch = {
    copySampleApp: actions.copySampleApp,
    unloadApps: appsActions.unloadApps,
};

const mapState = (state: RootState) => ({
    templateStore: state.templateStore,
});

const SerializedAppTile = connect(mapState, mapDispatch)(withRouter(withTheme(SerializedAppTileImpl)));
export default SerializedAppTile;
